import { createServerFn } from "@tanstack/react-start";
import { eq } from "drizzle-orm";
import { z } from "zod";

import { requireAuth } from "@/auth/middleware";
import { getDb, schema } from "@/db/client";
import { assistantIssue, screenName, textIssue } from "./complaint-issue";
import { notifyComplaint } from "./complaint-notify";
import { markComplaintReported, recordComplaint } from "./complaints-store";
import { openIssue } from "./github-issues";

/**
 * «Ответ неверный» — жалоба на реплику ассистента.
 *
 * Таких случаев будет много: ассистент понимает свободную речь, а свободная
 * речь бесконечна. Ловить их по одному в переписке — значит терять половину:
 * менеджер, у которого не получилось, обычно не пишет, а обходит.
 *
 * Поэтому жалоба отправляется одним нажатием и уносит с собой всё, что нужно
 * для разбора: экран, дословную просьбу, ответ, что ассистент понял, разбирала
 * ли фразу модель или запасной разбор по ключевым словам, и в каком состоянии
 * была смета.
 *
 * **Куда она попадает — два разных адреса, и это главное здесь.** Всё, что
 * описывает проект компании — заказчик, название, суммы, рентабельность,
 * текст экрана со ставками, снимок, — остаётся в её собственной базе, в
 * таблице `complaints` (миграция 0014), под той же RLS и в том же ночном
 * дампе, что сметы. Разработчику продукта уходит указатель: номер жалобы,
 * идентификаторы сметы и реплики, шаг мастера, слова менеджера и коды.
 * Решение владельца от 19.09.2026: «на Git — только логика и интерфейсы, но
 * не данные».
 *
 * Канал указателя — задача в приватном репозитории продукта через GitHub API.
 * Выбран он не из любви к GitHub, а потому что это единственное место, куда
 * пишет продукт и откуда читает тот, кто чинит: задачу видно в списке, по ней
 * виден коммит, и она закрывается выкаткой. Не завелась задача — жалоба всё
 * равно записана в базе компании, и это видно по пустой отметке об отправке.
 */

/** Экран в момент жалобы: текст всегда, снимок — если собрался. */
const Capture = z
  .object({
    text: z.string().max(20_000).optional(),
    /** JPEG data-URL */
    image: z.string().max(3_000_000).optional(),
    imageError: z.string().max(200).optional(),
  })
  .optional();

const Payload = z.object({
  capture: Capture,
  estimateId: z.string().uuid(),
  /** реплика, на которую жалуются */
  messageId: z.string().max(64),
  /** что должно было получиться — своими словами; необязательно */
  note: z.string().max(1000).optional(),
  /** шаг мастера: материалы, бриф, расчёт, чистовик */
  screen: z.string().max(40),
  /** дословная просьба менеджера */
  ask: z.string().max(1000),
  /** что ответил ассистент */
  answer: z.string().max(2000),
  /** что он, по его словам, сделал */
  done: z.array(z.string().max(200)).max(8).optional(),
  /** фразу разбирал запасной разбор по ключевым словам, а не модель */
  fallback: z.boolean().optional(),
  /** хвост разговора: две-три реплики до жалобы */
  history: z
    .array(z.object({ from: z.enum(["user", "bot"]), text: z.string().max(400) }))
    .max(6)
    .optional(),
  /** состояние сметы в момент жалобы */
  state: z.object({
    projectType: z.string().max(100).optional(),
    blocks: z.number().int().nonnegative(),
    lines: z.number().int().nonnegative(),
    totalGross: z.number(),
    margin: z.number(),
  }),
});

const _reportChatIssue = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .inputValidator((d: unknown) => Payload.parse(d))
  .handler(async ({ data, context }): Promise<{ ok: boolean; id: number; error?: string }> => {
    const [row] = await getDb()
      .select({ title: schema.estimates.title, client: schema.estimates.client })
      .from(schema.estimates)
      .where(eq(schema.estimates.id, data.estimateId))
      .limit(1);

    // Всё, что про проект, — в базу компании. Снимок отдельной колонкой:
    // полтора мегабайта base64 внутри jsonb разбирались бы при каждом чтении.
    const { capture, ...rest } = data;
    const complaint = await recordComplaint({
      kind: "assistant",
      estimateId: data.estimateId,
      payload: {
        ...rest,
        email: context.auth.email,
        title: row?.title ?? "",
        client: row?.client ?? "",
        screenText: capture?.text,
        screenshotError: capture?.imageError,
      },
      screenshot: capture?.image,
    });

    const where = screenName(data.screen, "");
    const text = assistantIssue(complaint.id, {
      screen: where,
      estimateId: data.estimateId,
      messageId: data.messageId,
      ask: data.ask,
      note: data.note,
      fallback: data.fallback,
    });
    const issue = await openIssue(text.title, text.body);
    if (issue.ok) await markComplaintReported(complaint.id, issue.url);
    // Жалоба записана у компании в любом случае — падает только указатель.
    else console.warn(`[complaint ${complaint.id}] указатель не ушёл: ${issue.error}`);

    await notifyComplaint(complaint.id, {
      kind: "assistant",
      screen: where,
      words: data.ask,
      issueUrl: issue.ok ? issue.url : undefined,
      issueError: issue.ok ? undefined : issue.error,
    });

    return issue.ok
      ? { ok: true, id: complaint.id }
      : { ok: false, id: complaint.id, error: issue.error };
  });

export const reportChatIssue = _reportChatIssue;

/**
 * Жалоба на что угодно на экране — по выделенному тексту.
 *
 * Ассистент — не единственное, что может быть не так: формулировка в
 * брифе, подпись колонки, текст предупреждения, число в своде. Раньше про
 * это писали в переписку, если писали. Теперь: выделил, нажал, объяснил —
 * и жалоба заведена тем же путём, что жалоба на ассистента.
 *
 * Выделенный текст в задачу не идёт: выделяют и подпись колонки, и сумму
 * со ставкой исполнителя, а отличить одно от другого нельзя. Он остаётся в
 * базе компании вместе с экраном и снимком; наружу уходят слова менеджера и
 * адрес страницы.
 */
const TextPayload = z.object({
  capture: Capture,
  /** адрес страницы: по нему видно экран и калькулятор */
  path: z.string().max(300),
  /** шаг мастера, если это калькулятор */
  screen: z.string().max(40).optional(),
  estimateId: z.string().uuid().optional(),
  /** что выделили — дословно */
  selection: z.string().min(1).max(2000),
  /** ближайшая подпись или заголовок — где именно это на экране */
  near: z.string().max(200).optional(),
  /** что не так, своими словами */
  note: z.string().max(1000).optional(),
});

const _reportTextIssue = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .inputValidator((d: unknown) => TextPayload.parse(d))
  .handler(async ({ data, context }): Promise<{ ok: boolean; id: number; error?: string }> => {
    let project = "";
    if (data.estimateId) {
      const [row] = await getDb()
        .select({ title: schema.estimates.title, client: schema.estimates.client })
        .from(schema.estimates)
        .where(eq(schema.estimates.id, data.estimateId))
        .limit(1);
      if (row) project = `${row.title || "без названия"} · ${row.client || "заказчик не указан"}`;
    }

    const { capture, ...rest } = data;
    const complaint = await recordComplaint({
      kind: "text",
      estimateId: data.estimateId,
      payload: {
        ...rest,
        email: context.auth.email,
        project,
        screenText: capture?.text,
        screenshotError: capture?.imageError,
      },
      screenshot: capture?.image,
    });

    const where = screenName(data.screen, data.path);
    const text = textIssue(complaint.id, {
      screen: where,
      path: data.path,
      estimateId: data.estimateId,
      note: data.note,
    });
    const issue = await openIssue(text.title, text.body, ["жалоба", "текст"]);
    if (issue.ok) await markComplaintReported(complaint.id, issue.url);
    else console.warn(`[complaint ${complaint.id}] указатель не ушёл: ${issue.error}`);

    // Слова менеджера — то же исключение, что в задаче: без них алерт
    // бессмысленн. Выделенный текст не берём: выделяют и подпись колонки, и
    // ставку исполнителя, а отличить одно от другого нельзя.
    await notifyComplaint(complaint.id, {
      kind: "text",
      screen: where,
      words: data.note,
      issueUrl: issue.ok ? issue.url : undefined,
      issueError: issue.ok ? undefined : issue.error,
    });

    return issue.ok
      ? { ok: true, id: complaint.id }
      : { ok: false, id: complaint.id, error: issue.error };
  });

export const reportTextIssue = _reportTextIssue;
